import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { FiServer, FiLayers, FiFilter, FiPhoneCall, FiTag, FiCalendar, FiChevronDown, FiRotateCcw, FiRefreshCw, FiDownload } from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import SkillPicker from '../components/SkillPicker';
import CallResults from '../components/CallResults';
import LeadPushModal from '../components/LeadPushModal';
import { dialerServers, todayInIndia } from '../utils/dialerFilterOptions';
import { dialerCrmRequest, fetchAllCallRecords, downloadCallExcel, getDialerCrmSession, requestError, SESSION_KEY } from '../utils/dialerCrmApi';
import './DialerCampaign.scss';

const initialFilters = () => {
    const today = todayInIndia();
    return { server: '', campaign: [], callType: 'IN', disposition: [], startDate: `${today}T00:00`, endDate: `${today}T23:59:59`, skills: [] };
};
const defaultCallType = server => dialerServers.find(item => item.key === server)?.type === 'convox' ? 'incoming' : 'IN';
const rangeValue = value => value.length === 16 ? `${value}:00` : value;
const validRange = filters => filters.startDate && filters.endDate && rangeValue(filters.startDate) <= rangeValue(filters.endDate);

export function CampaignSelector() {
    const [filters, setFilters] = useState(initialFilters);
    const [collapsed, setCollapsed] = useState(false);
    const [errors, setErrors] = useState({});
    const [options, setOptions] = useState({ campaigns: [], callTypes: [], skills: [], dispositions: [], campaignSkills: {} });
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [optionsError, setOptionsError] = useState('');
    const [retry, setRetry] = useState(0);
    const [applied, setApplied] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [resultError, setResultError] = useState('');
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState('');
    const [matching, setMatching] = useState(false);
    const [matchError, setMatchError] = useState('');
    const [pushOpen, setPushOpen] = useState(false);
    const [pickerVersion, setPickerVersion] = useState(0);
    const serverInput = useRef(null);
    const requestNumber = useRef(0);
    const navigate = useNavigate();
    const selectedServer = dialerServers.find(item => item.key === filters.server);
    const supported = ['ngucc', 'verb', 'convox'].includes(selectedServer?.type);
    const campaigns = options.campaigns;
    const skills = !filters.campaign.length ? options.skills : [...new Set(filters.campaign.flatMap(campaign =>
        Array.isArray(options.campaignSkills[campaign]) ? options.campaignSkills[campaign] : []))]
        .sort((a, b) => a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' }));

    const api = async (endpoint, data) => {
        const session = getDialerCrmSession();
        if (!session) { navigate('/', { replace: true }); throw new Error('Please log in again'); }
        try {
            const response = await dialerCrmRequest(endpoint, data, session.token);
            if (response.status !== 1) throw new Error(response.msg || 'Unable to load dialer data');
            return response.data;
        } catch (error) {
            if (error.response?.status === 401) { sessionStorage.removeItem(SESSION_KEY); navigate('/', { replace: true }); }
            throw error;
        }
    };
    const message = error => error.response ? requestError(error) : error.message || requestError(error);

    useEffect(() => {
        let active = true;
        setOptions({ campaigns: [], callTypes: [], skills: [], dispositions: [], campaignSkills: {} }); setOptionsError('');
        if (!supported || !validRange(filters)) { setOptionsLoading(false); return undefined; }
        setOptionsLoading(true);
        api('call-options', { server: filters.server, startDate: filters.startDate, endDate: filters.endDate }).then(data => {
            if (active) setOptions({ campaigns: data.campaigns || [], callTypes: data.callTypes || [], skills: data.skills || [], dispositions: data.dispositions || [], campaignSkills: data.campaignSkills || {} });
        }).catch(error => { if (active) setOptionsError(message(error)); })
            .finally(() => { if (active) setOptionsLoading(false); });
        return () => { active = false; };
        // Campaign-to-skill membership is loaded with the same server/date-scoped options.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.server, filters.startDate, filters.endDate, supported, retry]);

    useEffect(() => () => { requestNumber.current += 1; }, []);

    const clearResults = () => {
        setPushOpen(false);
        requestNumber.current += 1; setApplied(null); setResult(null); setResultError(''); setExportError(''); setMatchError(''); setLoading(false); setMatching(false);
    };
    const update = (key, value) => {
        const changesOptions = ['startDate', 'endDate'].includes(key);
        const nextValue = changesOptions ? value.replace(/\.\d+$/, '') : value;
        setFilters(current => ({ ...current, ...(changesOptions ? { campaign: [], callType: defaultCallType(current.server), disposition: [], skills: [] } : {}),
            ...(key === 'campaign' ? { skills: [] } : {}), [key]: nextValue }));
        setErrors({}); clearResults();
        if (changesOptions || key === 'campaign') setPickerVersion(version => version + 1);
    };
    const changeServer = value => {
        setFilters(current => ({ ...current, server: value, campaign: [], callType: defaultCallType(value), disposition: [], skills: [] }));
        setErrors({}); clearResults(); setPickerVersion(version => version + 1);
    };
    const loadCalls = async snapshot => {
        const number = ++requestNumber.current;
        setLoading(true); setResultError(''); setExportError(''); setMatchError(''); setMatching(false);
        try {
            const session = getDialerCrmSession();
            if (!session) { navigate('/', { replace: true }); throw new Error('Please log in again'); }
            const data = await fetchAllCallRecords(snapshot, session.token);
            if (requestNumber.current === number) setResult(data);
        } catch (error) {
            if (error.response?.status === 401) { sessionStorage.removeItem(SESSION_KEY); navigate('/', { replace: true }); }
            if (requestNumber.current === number) setResultError(message(error));
        }
        finally { if (requestNumber.current === number) setLoading(false); }
    };
    const apply = event => {
        event.preventDefault();
        const nextErrors = {};
        if (!supported) nextErrors.server = 'Please select a dialer server.';
        if (!filters.startDate) nextErrors.startDate = 'Choose a start date and time.';
        if (!filters.endDate) nextErrors.endDate = 'Choose an end date and time.';
        if (filters.startDate && filters.endDate && !validRange(filters)) nextErrors.endDate = 'End date/time must be on or after start date/time.';
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) { clearResults(); return; }
        const snapshot = { ...filters, campaign: [...filters.campaign], skills: [...filters.skills] };
        setApplied(snapshot); setResult(null); loadCalls(snapshot);
    };
    const exportExcel = async () => {
        if (!applied || exporting) return;
        const session = getDialerCrmSession();
        if (!session) { navigate('/', { replace: true }); return; }
        setExporting(true); setExportError('');
        try { await downloadCallExcel(applied, session.token, result); }
        catch (error) {
            if (error.response?.status === 401) { sessionStorage.removeItem(SESSION_KEY); navigate('/', { replace: true }); }
            setExportError(message(error));
        } finally { setExporting(false); }
    };
    const findUnmatched = async () => {
        if (!result?.total || result.kind === 'unmatched-calls' || loading || matching || exporting) return;
        const number = ++requestNumber.current;
        setMatching(true); setMatchError(''); setExportError('');
        try {
            const mobiles = [...new Set(result.rows.map(row => String(row.phonenumber ?? '')))];
            const data = await api('unmatched-calls', { mobiles });
            if (!Array.isArray(data?.mobiles) || data.kind !== 'unmatched-calls' || !data.matchRange) {
                throw new Error('Unmatched call data was not returned. Please retry.');
            }
            const unmatched = new Set(data.mobiles);
            const rows = result.rows.filter(row => unmatched.has(String(row.phonenumber ?? '')));
            if (requestNumber.current === number) setResult({ ...result, kind: data.kind, matchRange: data.matchRange, rows, total: rows.length });
        } catch (error) {
            if (requestNumber.current === number) setMatchError(message(error));
        } finally { if (requestNumber.current === number) setMatching(false); }
    };
    const reset = () => {
        setFilters(initialFilters()); setErrors({}); clearResults(); setPickerVersion(version => version + 1); serverInput.current?.focus();
    };

    return <section className="crm-campaign-page">
        <form className="crm-campaign-card" noValidate onSubmit={apply}>
            <div className="crm-campaign-card-title"><span className="crm-filter-icon"><FiFilter /></span>
                <h2>Advanced Filters</h2><p>Search all calls</p>
                <button type="button" className="crm-filter-collapse" aria-expanded={!collapsed} aria-controls="advanced-filter-body"
                    onClick={() => setCollapsed(value => !value)}>{collapsed ? 'Expand' : 'Collapse'}<FiChevronDown /></button>
            </div>
            <div className="crm-filter-body" id="advanced-filter-body" hidden={collapsed}>
                <div className="crm-campaign-fields">
                    <div className="crm-campaign-field">
                        <label htmlFor="dialer-server"><FiServer /> Select Server <span className="crm-required">*</span></label>
                        <select id="dialer-server" ref={serverInput} value={filters.server} onChange={event => changeServer(event.target.value)} aria-invalid={!!errors.server}>
                            <option value="">Select dialer server</option>
                            {dialerServers.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
                        </select>{errors.server && <small role="alert">{errors.server}</small>}
                    </div>
                    <div className="crm-campaign-field">
                        <label htmlFor="start-date" title="India Standard Time (IST)"><FiCalendar /> Start Date &amp; Time <span className="crm-required">*</span></label>
                        <input id="start-date" type="datetime-local" step="1" value={filters.startDate} max={filters.endDate || undefined} aria-invalid={!!errors.startDate}
                            onChange={event => update('startDate', event.target.value)} />
                        {errors.startDate && <small role="alert">{errors.startDate}</small>}
                    </div>
                    <div className="crm-campaign-field">
                        <label htmlFor="end-date" title="India Standard Time (IST)"><FiCalendar /> End Date &amp; Time <span className="crm-required">*</span></label>
                        <input id="end-date" type="datetime-local" step="1" value={filters.endDate} min={filters.startDate || undefined} aria-invalid={!!errors.endDate}
                            onChange={event => update('endDate', event.target.value)} />
                        {errors.endDate && <small role="alert">{errors.endDate}</small>}
                    </div>
                    <div className="crm-campaign-field">
                        <label id="campaign-label" htmlFor="campaign-trigger"><FiLayers /> Campaign Name <span className="crm-field-optional">Multiple selection</span></label>
                        <SkillPicker key={`campaign-${filters.server}-${filters.startDate}-${filters.endDate}`} idPrefix="campaign" singular="campaign" plural="campaigns"
                            options={campaigns} selected={filters.campaign} showSelectedTags={false} emptyLabel={optionsLoading ? 'Loading campaigns...' : !filters.server ? 'Select server and dates first' : 'All campaigns'}
                            disabled={!supported || optionsLoading || !!optionsError || !validRange(filters)} onChange={values => update('campaign', values)} />
                        {errors.campaign && <small role="alert">{errors.campaign}</small>}
                    </div>
                    <div className="crm-campaign-field">
                        <label htmlFor="call-type"><FiPhoneCall /> Call Type</label>
                        <select id="call-type" value={filters.callType} disabled={!supported || optionsLoading || !!optionsError || !validRange(filters)} onChange={event => update('callType', event.target.value)}>
                            <option value="">All call types</option>
                            {[defaultCallType(filters.server), ...options.callTypes.filter(value => value !== defaultCallType(filters.server))].map(value => <option key={value} value={value}>{value}</option>)}
                        </select>
                    </div>
                    {selectedServer?.type !== 'convox' && <div className="crm-campaign-field">
                        <label id="disposition-label" htmlFor="disposition-trigger"><FiTag /> Disposition <span className="crm-field-optional">Multiple selection</span></label>
                        <SkillPicker key={`disposition-${pickerVersion}`} idPrefix="disposition" singular="disposition" plural="dispositions"
                            options={options.dispositions} selected={filters.disposition} showSelectedTags={false}
                            disabled={!supported || optionsLoading || !!optionsError || !validRange(filters)} onChange={values => update('disposition', values)} />
                    </div>}
                    <div className="crm-campaign-field crm-skill-section">
                        <label id="skill-label" htmlFor="skill-trigger"><FiLayers /> Select Skills <span className="crm-field-optional">Multiple selection</span></label>
                        <SkillPicker key={pickerVersion} options={skills} selected={filters.skills} showSelectedTags={false}
                            disabled={!supported || optionsLoading || !!optionsError || !validRange(filters)} onChange={value => update('skills', value)} />
                    </div>
                </div>
                {selectedServer && !supported && <p className="crm-report-notice">Please select a supported dialer server.</p>}
                {optionsError && <div className="crm-report-error" role="alert">{optionsError}
                    <button type="button" onClick={() => setRetry(value => value + 1)}><FiRefreshCw /> Retry options</button>
                </div>}
                {supported && validRange(filters) && !optionsLoading && !optionsError && !campaigns.length && <p className="crm-report-notice">No campaigns found in this date range.</p>}
                <div className="crm-filter-actions"><div>
                    <button type="submit" className="crm-filter-apply" disabled={loading || matching || !supported || optionsLoading}><FiFilter /> {loading ? 'Loading...' : 'Apply Filters'}</button>
                    <button type="button" className="crm-filter-reset" onClick={reset}><FiRotateCcw /> Reset</button>
                    {/* <button type="button" className="crm-excel-button" onClick={exportExcel} disabled={!result?.total || loading || matching || exporting || !!resultError}>
                        <FiDownload /> {exporting ? 'Preparing Excel...' : 'Download Excel'}
                    </button> */}
                </div>
                    <div className="crm-unmatched-actions"><button type="button" className="crm-match-button" onClick={findUnmatched}
                        disabled={!result?.total || loading || matching || exporting || !!resultError || result?.kind === 'unmatched-calls'}>
                        <FiRefreshCw /> {matching ? 'Checking CRM Orders...' : 'Show Unmatched Calls'}
                    </button>
                    <button type="button" className="crm-push-button" onClick={() => setPushOpen(true)}
                        disabled={result?.kind !== 'unmatched-calls' || !result?.total || loading || matching || exporting || !!resultError}>Push Leads</button></div>
                </div>
                {matchError && <p className="crm-report-error" role="alert">{matchError}</p>}
            </div>
        </form>
        {applied && <CallResults data={result} loading={loading} error={resultError} exportError={exportError}
            onRetry={() => loadCalls(applied)} />}
        {result?.kind === 'unmatched-calls' && result.total > 0 && <LeadPushModal key={requestNumber.current}
            show={pushOpen} onClose={() => setPushOpen(false)} rows={result.rows} api={api} />}
    </section>;
}

export default function DialerCampaign() {
    return <DashboardLayout>{() => <><Helmet><title>Satkartar | Dialer Call Report</title></Helmet><CampaignSelector /></>}</DashboardLayout>;
}
